import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { CostCentreResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper } from '../utils';
import { ICONS } from '../icons';
import { CostCentreDetailsSectionHandler } from './details.section';
import { ContactsSectionHandler } from '../shared/contacts.section';
import { AssetGroupingService } from 'app/services';
import { AssetListSectionHandler, UserListSectionHandler } from '../shared/list.section';
import { RegisterEntityHandler } from '../../entities';


@RegisterEntityHandler('costcentre')
@Injectable()
export class CostCentreHandler extends EntityHandler<CostCentreResponse> {

    currentEntity: CostCentreResponse;

    constructor(
        admin: AdminEditorService,
        private grouping: AssetGroupingService
    ) {
        super(admin);
        this.listMode = 'tree';
    }

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        // we can't paginate tree items, so hard code a limit of 250 and hope it's enough
        offset = 0;
        limit = 250;
        return this.admin.getCached(`listCostCentres/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listCostCentres(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'costcentre',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.COSTCENTRES'),
        }));
    }

    getEntity(id: string): Promise<CostCentreResponse> {
        return this.admin.getCached(`getCostCentre/${id}`, api => api.entities.getCostCentre(id), 10).then(result => {
            this.currentEntity = result;
            return result;
        });
    }

    createEntity(ownerId: string, entity: any): Promise<CostCentreResponse> {
        entity.ownerId = ownerId;
        this.grouping.clear(); // clear our grouping cache after an update
        return this.admin.applyUpdate(api => api.entities.createCostCentre(entity));
    }

    setEntityState(entity: any, state: string): Promise<any> {
        if (state === 'deleted') {
            this.grouping.clear(); // clear our grouping cache after an update
            this.currentEntity = null;
            return this.admin.applyUpdate(api => api.entities.deleteCostCentre(entity.id));
        }
        return Promise.reject('Only deletion supported');
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.COSTCENTRE.COLUMNS');
        const columns: IKuiTableCol[] = [
            grid.IconTextColumn('name', 'NAME', (row) => {
                return row.folder ? ICONS.folder : ICONS.costcentre;
            }),
            // grid.TextColumn('assets', 'ASSETS', 80), TODO: add back when the backend can return a count
            // grid.TextColumn('users', 'USERS', 80), TODO: add back when the backend can return a count
            grid.TimeAgoColumn('entity.modifiedDate', 'MODIFIED'),
        ];
        return Promise.resolve(columns);
    }

    getEditorSections(ownerId: string, _entity: any): Promise<SectionHandlerGroup<CostCentreResponse>[]> {
        const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ASSET.COLUMNS');
        const { id, name } = this.currentEntity;
        return Promise.resolve([{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new CostCentreDetailsSectionHandler(this.admin, ownerId, this.grouping, false),
                new ContactsSectionHandler(this.admin, ownerId, 'costcentre'),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.ASSETS'),
            sections: [
                new AssetListSectionHandler(this.admin, ownerId, ['add', 'delete'], this.grouping, 'costCentre', [grid.IdNameComboColumn('costCentre', 'COST_CENTRE', [], 150)], { id, name }, async () => this.replaceCostCentreModal()),
            ],
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.USERS'),
            sections: [
                new UserListSectionHandler(this.admin, ownerId, ['add', 'delete'], 'costCentre', [grid.IdNameComboColumn('costCentre', 'COST_CENTRE', [], 150)], { id, name }, async () => this.replaceCostCentreModal()),
            ],
        }]);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<CostCentreResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'costcentre',
            entity: {
                parent: this.currentEntity ? { id: this.currentEntity.id, name: this.currentEntity.name } : null,
            },
            sections: [
                new CostCentreDetailsSectionHandler(this.admin, ownerId, this.grouping, true),
            ],
        });
    }

    private async replaceCostCentreModal(): Promise<{ id: string, name: string }> {
        const result = await this.admin.showFormModal({
            groups: [{
                name: this.admin.i18n.instant('ADMIN.EDITORS.SHARED.LIST.REPLACE_COST_CENTRE'),
                fields: [{
                    type: 'searchselect',
                    id: 'group',
                    title: this.admin.i18n.instant('ADMIN.ENTITIES.COSTCENTRES'),
                    max: 1,
                    required: true,
                    options: {
                        searchItemPromise: groupId => this.admin.getCached(`getCostCentre/${groupId}`, api => api.entities.getCostCentre(groupId)),
                        searchListPromise: async (_limit, filter) => ({ items: filter ? this.grouping.getCostCentres() : this.grouping.getCostCentresAsTree() } as any),
                    },
                }],
            }],
        }, { group: null });

        if (!result) { return null; }
        const groups = await this.grouping.getCostCentres();
        const group = groups.find(x => x.id === result.group.id);
        return { id: group.id, name: group.name };
    }
}
